import type { SupportingPagesV2RowResult } from '../services/dataforseo';
import type { DomainTopicUrlRow } from './topic-supporting-pages';
import { compareSupportingPagesByRelevance, normalizeUrlForCompare } from './topic-supporting-pages';
import { hasSectionGapsForV2Row } from './section-gaps';

export interface TopicOwnershipDomainBlock {
  domain: string;
  isYourSite: boolean;
  rows: DomainTopicUrlRow[];
}

export interface SupportingPagesUnifiedColumn {
  domain: string;
  isYourSite: boolean;
  heroUrl: string;
  heroRow: DomainTopicUrlRow | null;
  supportingRows: DomainTopicUrlRow[];
  v2Row: SupportingPagesV2RowResult | null;
  /** Set when column was injected from SERP (not topic snapshot). */
  serpSource?: 'marked' | 'v2';
}

function normalizeDomain(domain: string): string {
  return domain.toLowerCase().trim().replace(/^www\./, '');
}

function rowForUrl(rows: DomainTopicUrlRow[], url: string): DomainTopicUrlRow | null {
  const key = normalizeUrlForCompare(url);
  if (!key) return null;
  return rows.find((r) => normalizeUrlForCompare(r.url) === key) ?? null;
}

/** List rows below the hero — excludes the hero URL when it appears in diagnostics. */
function rowsBelowHero(rows: DomainTopicUrlRow[], heroUrl: string): DomainTopicUrlRow[] {
  const heroKey = normalizeUrlForCompare(heroUrl);
  if (!heroKey) return rows;
  return rows.filter((r) => normalizeUrlForCompare(r.url) !== heroKey);
}

function v2RowForDomain(
  v2Rows: SupportingPagesV2RowResult[] | undefined,
  domain: string,
  extractDomain: (url: string) => string,
  preferredCompetitorUrl?: string | null
): SupportingPagesV2RowResult | null {
  const key = normalizeDomain(domain);
  if (!key || !v2Rows?.length) return null;

  if (preferredCompetitorUrl) {
    const prefKey = normalizeUrlForCompare(preferredCompetitorUrl);
    const prefRow = v2Rows.find(
      (row) =>
        normalizeDomain(extractDomain(row.competitorUrl)) === key &&
        normalizeUrlForCompare(row.competitorUrl) === prefKey
    );
    if (prefRow) return prefRow;
  }

  return (
    v2Rows.find((row) => normalizeDomain(extractDomain(row.competitorUrl)) === key) ?? null
  );
}

export function buildSupportingPagesUnifiedColumns(input: {
  domains: TopicOwnershipDomainBlock[];
  focalUrl: string;
  v2Rows?: SupportingPagesV2RowResult[];
  extractDomain: (url: string) => string;
  filterRows: (rows: DomainTopicUrlRow[]) => DomainTopicUrlRow[];
  /** Prefer this competitor URL as hero when domain matches (marked rival on SERP). */
  preferredCompetitorUrlByDomain?: Record<string, string>;
}): SupportingPagesUnifiedColumn[] {
  const focal = (input.focalUrl || '').trim();
  const columns: SupportingPagesUnifiedColumn[] = [];

  for (const block of input.domains) {
    // Sorted here rather than trusted from the payload: `supportingRows[0]` is
    // the hero, and a builder that depends on its input arriving pre-sorted is
    // one backend change away from picking an arbitrary page. The comparator is
    // the shared one, so this column and the "Compare to…" list below it cannot
    // disagree about which page is most relevant.
    const supportingRows = [...input.filterRows(block.rows)].sort(
      compareSupportingPagesByRelevance
    );

    if (block.isYourSite) {
      const yourSiteRows = rowsBelowHero(supportingRows, focal);
      columns.push({
        domain: block.domain,
        isYourSite: true,
        heroUrl: '',
        heroRow: null,
        supportingRows: yourSiteRows,
        v2Row: null
      });
      continue;
    }

    /**
     * The hero is the domain's most relevant page, and relevance is
     * `focalMatchScore` — the rule `topic-supporting-pages.ts` states and
     * FW-URL-05 shipped.
     *
     * The order below is the fix for two bugs that were one line:
     *
     * 1. A v2 eval row used to win outright (`v2Row?.competitorUrl || ...`),
     *    and `v2RowForDomain` resolves with `.find()` — the first eval for the
     *    domain in ARRAY order, never scored. So the hero was whichever page
     *    happened to be evaluated first.
     * 2. Those evals arrive after first paint (`ensureMarkedSerpCompetitorEvals`),
     *    so the card rendered the correctly-ranked page, then visibly replaced
     *    it with an unranked one a few seconds later. On ManyRequests'
     *    `/blog/cost-performance-index`, productive.io's true peer
     *    (`/blog/cost-performance-index-in-project-management/`, ~3.0k/mo)
     *    was swapped for `/blog/to-complete-performance-index/`.
     *
     * The SERP URL does not win either, and that is the second half of the fix.
     * `preferredCompetitorUrlByDomain` is the page this domain ranks with on
     * THIS query -- productive.io at #8 with `/blog/to-complete-performance-index/`
     * -- and it was overriding the topical hero. But this card's own subhead
     * says "top page per domain for this URL's focal topics", and the page a
     * rival ranks with today is a different question, answered by "Who's
     * beating you?" one card down. Two cards answering the same question, one
     * of them under a heading that promises the other, is how the wrong page
     * came to be labelled the main competing page.
     *
     * It stays as the fallback: a domain with no topic rows at all is exactly
     * the case `supportingPagesSerpSeeds` injects a SERP URL for, and there the
     * SERP URL is the only page we know about.
     *
     * The eval only ANNOTATES the hero now; it no longer picks it.
     */
    const preferredUrl = input.preferredCompetitorUrlByDomain?.[normalizeDomain(block.domain)];
    const v2Row = v2RowForDomain(input.v2Rows, block.domain, input.extractDomain, preferredUrl);
    const heroUrl = supportingRows[0]?.url || preferredUrl || v2Row?.competitorUrl || '';
    columns.push({
      domain: block.domain,
      isYourSite: false,
      heroUrl,
      heroRow: rowForUrl(supportingRows, heroUrl),
      supportingRows: rowsBelowHero(supportingRows, heroUrl),
      v2Row
    });
  }

  return columns.sort((a, b) => {
    if (a.isYourSite !== b.isYourSite) return Number(b.isYourSite) - Number(a.isYourSite);
    return a.domain.localeCompare(b.domain);
  });
}

export interface SerpCompetitorColumnSeed {
  domain: string;
  url: string;
  source: 'marked';
}

function columnHasDomain(columns: SupportingPagesUnifiedColumn[], domain: string): boolean {
  const key = normalizeDomain(domain);
  return columns.some((col) => !col.isYourSite && normalizeDomain(col.domain) === key);
}

function isMarkedDomain(domain: string, markedDomains: string[]): boolean {
  const key = normalizeDomain(domain);
  if (!key || !markedDomains.length) return false;
  return markedDomains.some((m) => normalizeDomain(m) === key);
}

/**
 * Add competitor columns for marked rivals on this page's SERP when the topic
 * snapshot has no rows for that domain. Top SERP rivals belong on Who's beating
 * you? / Quick Compare — not this card.
 */
export function augmentSupportingPagesColumns(input: {
  columns: SupportingPagesUnifiedColumn[];
  v2Rows?: SupportingPagesV2RowResult[];
  serpSeeds: SerpCompetitorColumnSeed[];
  extractDomain: (url: string) => string;
  markedDomains: string[];
}): SupportingPagesUnifiedColumn[] {
  const out = [...input.columns];

  const pushCompetitorColumn = (
    domain: string,
    preferredUrl: string,
    source: 'marked' | 'v2'
  ): void => {
    const key = normalizeDomain(domain);
    const pageUrl = (preferredUrl || '').trim();
    if (!key || !pageUrl || columnHasDomain(out, key)) return;
    if (!isMarkedDomain(key, input.markedDomains)) return;

    const v2Row = v2RowForDomain(input.v2Rows, key, input.extractDomain, pageUrl);

    out.push({
      domain: key,
      isYourSite: false,
      heroUrl: v2Row?.competitorUrl || pageUrl,
      heroRow: null,
      supportingRows: [],
      v2Row,
      serpSource: source
    });
  };

  for (const seed of input.serpSeeds) {
    pushCompetitorColumn(seed.domain, seed.url, seed.source);
  }

  for (const row of input.v2Rows || []) {
    const domain = input.extractDomain(row.competitorUrl);
    pushCompetitorColumn(domain, row.competitorUrl, 'v2');
  }

  return out.sort((a, b) => {
    if (a.isYourSite !== b.isYourSite) return Number(b.isYourSite) - Number(a.isYourSite);
    return a.domain.localeCompare(b.domain);
  });
}

/** Attach v2 eval rows to competitor columns that were injected without one yet. */
export function attachV2RowsToSupportingPageColumns(
  columns: SupportingPagesUnifiedColumn[],
  v2Rows: SupportingPagesV2RowResult[] | undefined,
  extractDomain: (url: string) => string
): SupportingPagesUnifiedColumn[] {
  return columns.map((col) => {
    if (col.isYourSite || col.v2Row) return col;
    const v2Row = v2RowForDomain(v2Rows, col.domain, extractDomain, col.heroUrl);
    if (!v2Row) return col;
    return { ...col, v2Row };
  });
}

/**
 * Whether any rival has a section this page is missing.
 *
 * `sectionGapsForV2Row`, not the stored `missingSectionCount`. The stored
 * number counts groups BEFORE the filters — measured on 326 live snapshots it
 * disagrees on 22 of them — so this predicate could report a gap on a listicle
 * whose every "section" is a tool the rival lists, and the card underneath it
 * would then show nothing. See `utils/section-gaps`.
 */
export function hasMissingSectionGaps(v2Rows: SupportingPagesV2RowResult[] | undefined | null): boolean {
  return (v2Rows || []).some((row) => hasSectionGapsForV2Row(row));
}

/**
 * Whether any rival outranks this page on a query it already covers.
 *
 * Counts gaps that can SAY why, because that is what the card renders: a gap
 * with no evidence line is dropped there — the evidence is the finding, and a
 * row that cannot explain itself is a row nobody can act on.
 *
 * Today every gap gets an evidence line at build time, so this predicate and
 * that filter agree on all live data. It is written this way anyway: a
 * predicate that counts rows the renderer drops is a badge over an empty card
 * waiting for one input to change, and this codebase has now shipped that bug
 * three times.
 */
export function hasRankDrivenGaps(v2Rows: SupportingPagesV2RowResult[] | undefined | null): boolean {
  return (v2Rows || []).some((row) =>
    (row.rankDrivenGaps ?? []).some((gap) => (gap?.evidenceLine || '').trim())
  );
}

export function hasListInventoryGaps(v2Rows: SupportingPagesV2RowResult[] | undefined | null): boolean {
  return (v2Rows || []).some(
    (row) =>
      !!row.listInventoryDiff?.detected && (row.listInventoryDiff.competitorOnly?.length ?? 0) > 0
  );
}

/** Competitor columns from v2 eval when v1 topic snapshot is missing or empty. */
export function buildSupportingPagesV2OnlyColumns(input: {
  focalUrl: string;
  yourSiteDomain: string;
  v2Rows: SupportingPagesV2RowResult[];
  extractDomain: (url: string) => string;
}): SupportingPagesUnifiedColumn[] {
  const columns: SupportingPagesUnifiedColumn[] = [];
  if (input.yourSiteDomain) {
    columns.push({
      domain: input.yourSiteDomain,
      isYourSite: true,
      heroUrl: '',
      heroRow: null,
      supportingRows: [],
      v2Row: null
    });
  }
  for (const row of input.v2Rows) {
    const domain = normalizeDomain(input.extractDomain(row.competitorUrl));
    if (!domain) continue;
    columns.push({
      domain,
      isYourSite: false,
      heroUrl: row.competitorUrl,
      heroRow: null,
      supportingRows: [],
      v2Row: row
    });
  }
  return columns.sort((a, b) => {
    if (a.isYourSite !== b.isYourSite) return Number(b.isYourSite) - Number(a.isYourSite);
    return a.domain.localeCompare(b.domain);
  });
}
